import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../components/ui/dialog";
import { Switch } from "../components/ui/switch";
import { toast } from "sonner";
import { Plus, ClipboardList, Trash2, Wand2, Sparkles } from "lucide-react";

const EMPTY = {
  employee_id: "", order_id: "", date: new Date().toISOString().slice(0,10),
  start_morning: "", end_morning: "", start_after: "", end_after: "",
  start_permission: "", end_permission: "", start_extra: "", end_extra: "",
  disease: false, holiday_hours: 0, availability: false, notes: "",
};

export default function ReportsPage() {
  const [items, setItems] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [orders, setOrders] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  // Auto-gen da timbrature
  const [autoOpen, setAutoOpen] = useState(false);
  const [autoEmp, setAutoEmp] = useState("");
  const [autoDate, setAutoDate] = useState(new Date().toISOString().slice(0, 10));
  const [autoBusy, setAutoBusy] = useState(false);
  const [autoStatus, setAutoStatus] = useState(null); // {kind, msg, lastDay?}
  // Batch giornaliero
  const [batchBusy, setBatchBusy] = useState(false);

  const load = async () => {
    setItems((await api.get("/reports")).data);
    setEmployees((await api.get("/employees")).data);
    setOrders((await api.get("/orders")).data);
  };
  useEffect(() => { load(); }, []);

  const up = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const toIso = (v) => v ? new Date(`${form.date}T${v}:00`).toISOString() : null;

  const generateFromAttendances = async () => {
    if (!autoEmp) { setAutoStatus({ kind: "error", msg: "Seleziona un dipendente" }); return; }
    setAutoBusy(true);
    setAutoStatus({ kind: "loading", msg: "Lettura timbrature in corso…" });
    try {
      const r = await api.post("/reports/from-attendances", { employee_id: autoEmp, date: autoDate });
      setAutoStatus({ kind: "ok", msg: `Rapportino ${r.data.action === "updated" ? "aggiornato" : "creato"} con ${r.data.attendances_used} timbrature` });
      toast.success(`Rapportino ${r.data.action === "updated" ? "aggiornato" : "creato"} · ${r.data.attendances_used} timbrature usate`);
      setTimeout(() => { setAutoOpen(false); setAutoStatus(null); load(); }, 1200);
    } catch (e) {
      const detail = e?.response?.data?.detail || "Errore generazione";
      // Estrae eventuale "Ultima timbratura disponibile: YYYY-MM-DD" dal detail
      const m = detail.match(/Ultima timbratura disponibile:\s*(\d{4}-\d{2}-\d{2})/);
      setAutoStatus({
        kind: e?.response?.status === 404 ? "empty" : "error",
        msg: detail,
        lastDay: m ? m[1] : null,
      });
    } finally { setAutoBusy(false); }
  };

  const runYesterdayBatch = async () => {
    setBatchBusy(true);
    try {
      const r = await api.post("/admin/auto-reports/run");
      const { target_date, reports_created, skipped_existing } = r.data || {};
      if (reports_created > 0) {
        toast.success(`✓ ${reports_created} rapportin${reports_created === 1 ? "o creato" : "i creati"} per il ${new Date(target_date).toLocaleDateString("it-IT")}`);
      } else if (skipped_existing > 0) {
        toast.info(`Tutti i ${skipped_existing} rapportini del ${new Date(target_date).toLocaleDateString("it-IT")} erano già presenti`);
      } else {
        toast.info(`Nessuna timbratura QR per il ${new Date(target_date).toLocaleDateString("it-IT")}`);
      }
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore esecuzione batch");
    } finally { setBatchBusy(false); }
  };

  const save = async () => {
    if (!form.employee_id) { toast.error("Seleziona un dipendente"); return; }
    try {
      await api.post("/reports", {
        ...form,
        holiday_hours: Number(form.holiday_hours) || 0,
        start_morning: toIso(form.start_morning), end_morning: toIso(form.end_morning),
        start_after: toIso(form.start_after), end_after: toIso(form.end_after),
        start_permission: toIso(form.start_permission), end_permission: toIso(form.end_permission),
        start_extra: toIso(form.start_extra), end_extra: toIso(form.end_extra),
        order_id: form.order_id || null,
      });
      toast.success("Rapportino creato");
      setForm(EMPTY); setOpen(false); load();
    } catch (e) { toast.error("Errore"); }
  };

  const del = async (id) => {
    if (!window.confirm("Eliminare il rapportino?")) return;
    await api.delete(`/reports/${id}`);
    toast.success("Eliminato"); load();
  };

  const empName = (id) => {
    const e = employees.find((x) => x.id === id);
    return e ? `${e.name} ${e.lastname}` : "—";
  };
  const ordName = (id) => orders.find((x) => x.id === id)?.order_code || "—";
  const hhmm = (iso) => iso ? new Date(iso).toLocaleTimeString("it-IT", {hour:"2-digit", minute:"2-digit"}) : "—";

  return (
    <div className="p-4 md:p-12" data-testid="reports-page">
      <div className="flex items-start justify-between mb-6">
        <div>
          <div className="text-xs uppercase tracking-widest text-[#757575] mb-2">Rapportini</div>
          <h1 className="font-display text-4xl">Rapportini giornalieri</h1>
          <p className="text-[#555] mt-1 text-sm">Mattina · Pomeriggio · Permessi · Straordinari · Malattia · Ferie · Reperibilità</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            onClick={runYesterdayBatch}
            disabled={batchBusy}
            data-testid="batch-yesterday-btn"
            variant="outline"
            className="rounded-full border-emerald-500 text-emerald-700 hover:bg-emerald-50 h-11"
            title="Crea automaticamente tutti i rapportini di ieri da timbrature QR"
          >
            <Sparkles className="w-4 h-4 mr-2"/> {batchBusy ? "Esecuzione…" : "Esegui batch di ieri"}
          </Button>
          <Button onClick={()=>setAutoOpen(true)} data-testid="auto-report-btn" variant="outline" className="rounded-full border-[#3B82F6] text-[#7C3AED] hover:bg-[#1E2347] h-11">
            <Wand2 className="w-4 h-4 mr-2"/> Genera da timbrature
          </Button>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="new-report-btn" className="rounded-full bg-[#3B82F6] hover:bg-[#7C3AED] h-11 btn-glow">
                <Plus className="w-4 h-4 mr-2"/> Nuovo rapportino
              </Button>
            </DialogTrigger>
          <DialogContent className="rounded-3xl border-0 max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle className="font-display text-2xl">Nuovo rapportino</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Dipendente *</Label>
                  <Select value={form.employee_id} onValueChange={(v)=>setForm({...form, employee_id: v})}>
                    <SelectTrigger className="rounded-xl mt-1"><SelectValue placeholder="Seleziona"/></SelectTrigger>
                    <SelectContent>
                      {employees.map((e) => <SelectItem key={e.id} value={e.id}>{e.name} {e.lastname}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Commessa</Label>
                  <Select value={form.order_id} onValueChange={(v)=>setForm({...form, order_id: v})}>
                    <SelectTrigger className="rounded-xl mt-1"><SelectValue placeholder="—"/></SelectTrigger>
                    <SelectContent>
                      {orders.map((o) => <SelectItem key={o.id} value={o.id}>{o.order_code} · {o.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div><Label>Data</Label><Input type="date" className="rounded-xl mt-1" value={form.date} onChange={up("date")}/></div>

              <div className="border-t border-[#1F2540] pt-3">
                <div className="text-xs uppercase tracking-widest text-[#3B82F6] font-semibold mb-2">Mattina</div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-[10px] uppercase tracking-widest text-[#999]">Inizio</Label>
                    <Input type="time" className="rounded-xl mt-1" value={form.start_morning} onChange={up("start_morning")}/>
                  </div>
                  <div>
                    <Label className="text-[10px] uppercase tracking-widest text-[#999]">Fine</Label>
                    <Input type="time" className="rounded-xl mt-1" value={form.end_morning} onChange={up("end_morning")}/>
                  </div>
                </div>
              </div>
              <div>
                <div className="text-xs uppercase tracking-widest text-[#3B82F6] font-semibold mb-2">Pomeriggio</div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-[10px] uppercase tracking-widest text-[#999]">Inizio</Label>
                    <Input type="time" className="rounded-xl mt-1" value={form.start_after} onChange={up("start_after")}/>
                  </div>
                  <div>
                    <Label className="text-[10px] uppercase tracking-widest text-[#999]">Fine</Label>
                    <Input type="time" className="rounded-xl mt-1" value={form.end_after} onChange={up("end_after")}/>
                  </div>
                </div>
              </div>
              <div>
                <div className="text-xs uppercase tracking-widest text-[#3B82F6] font-semibold mb-2">Permessi</div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-[10px] uppercase tracking-widest text-[#999]">Inizio</Label>
                    <Input type="time" className="rounded-xl mt-1" value={form.start_permission} onChange={up("start_permission")}/>
                  </div>
                  <div>
                    <Label className="text-[10px] uppercase tracking-widest text-[#999]">Fine</Label>
                    <Input type="time" className="rounded-xl mt-1" value={form.end_permission} onChange={up("end_permission")}/>
                  </div>
                </div>
              </div>
              <div>
                <div className="text-xs uppercase tracking-widest text-[#3B82F6] font-semibold mb-2">Straordinari</div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-[10px] uppercase tracking-widest text-[#999]">Inizio</Label>
                    <Input type="time" className="rounded-xl mt-1" value={form.start_extra} onChange={up("start_extra")}/>
                  </div>
                  <div>
                    <Label className="text-[10px] uppercase tracking-widest text-[#999]">Fine</Label>
                    <Input type="time" className="rounded-xl mt-1" value={form.end_extra} onChange={up("end_extra")}/>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 border-t border-[#1F2540] pt-3">
                <button
                  type="button"
                  onClick={()=>setForm({...form, disease: !form.disease})}
                  data-testid="report-toggle-disease"
                  className={`flex items-center justify-between rounded-xl px-3 py-3 border-2 transition-all ${form.disease ? "bg-red-500 text-white border-red-500 shadow-md shadow-red-500/30" : "bg-white text-[#0A0A0A] border-[#1F2540] hover:border-red-400"}`}
                >
                  <span className="flex items-center gap-2 text-sm font-bold">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>
                    Malattia
                  </span>
                  <span className={`text-[10px] uppercase tracking-widest font-bold ${form.disease ? "text-white" : "text-red-500"}`}>{form.disease ? "ON" : "OFF"}</span>
                </button>
                <button
                  type="button"
                  onClick={()=>setForm({...form, availability: !form.availability})}
                  data-testid="report-toggle-availability"
                  className={`flex items-center justify-between rounded-xl px-3 py-3 border-2 transition-all ${form.availability ? "bg-blue-600 text-white border-blue-600 shadow-md shadow-blue-500/30" : "bg-white text-[#0A0A0A] border-[#1F2540] hover:border-blue-400"}`}
                >
                  <span className="flex items-center gap-2 text-sm font-bold">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"/></svg>
                    Reperibilità
                  </span>
                  <span className={`text-[10px] uppercase tracking-widest font-bold ${form.availability ? "text-white" : "text-blue-600"}`}>{form.availability ? "ON" : "OFF"}</span>
                </button>
                <div>
                  <Label className="text-xs">Ore ferie</Label>
                  <Input type="number" className="rounded-xl mt-1 h-11" value={form.holiday_hours} onChange={up("holiday_hours")}/>
                </div>
              </div>

              <div><Label>Note</Label><Textarea className="rounded-xl mt-1" value={form.notes} onChange={up("notes")}/></div>
            </div>
            <DialogFooter><Button onClick={save} data-testid="report-save" className="rounded-full bg-[#3B82F6] hover:bg-[#7C3AED]">Salva rapportino</Button></DialogFooter>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      <div className="mb-6 p-4 rounded-2xl bg-gradient-to-r from-emerald-50 to-[#1E2347] border border-emerald-200 flex items-start gap-3" data-testid="auto-info-banner">
        <Sparkles className="w-5 h-5 text-emerald-600 mt-0.5 flex-shrink-0"/>
        <div className="text-sm flex-1">
          <div className="font-semibold text-emerald-900">Generazione automatica notturna attiva</div>
          <p className="text-xs text-[#555] mt-1">
            Ogni notte alle 23:00 (UTC) il sistema crea automaticamente i rapportini del giorno appena trascorso per tutti i dipendenti che hanno timbrato col QR. Verrai notificato via email con il riepilogo.
            Puoi anche eseguire il batch manualmente dal pulsante <strong>"Esegui batch di ieri"</strong> o generare un singolo rapportino con <strong>"Genera da timbrature"</strong>.
          </p>
        </div>
      </div>

      {/* Dialog auto-gen da timbrature */}
      <Dialog open={autoOpen} onOpenChange={setAutoOpen}>
        <DialogContent className="rounded-3xl border-0 max-w-md" data-testid="auto-report-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl flex items-center gap-2">
              <Wand2 className="w-6 h-6 text-[#3B82F6]"/> Genera da timbrature QR
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <p className="text-[#555] text-xs">Crea (o aggiorna) automaticamente il rapportino giornaliero per il dipendente, usando le timbrature QR del giorno. Mapping: <strong>Entrata→inizio mattina</strong>, <strong>Pausa pranzo→fine mattina</strong>, <strong>Fine pausa→inizio pomeriggio</strong>, <strong>Uscita→fine pomeriggio</strong>.</p>
            <div>
              <Label>Dipendente *</Label>
              <Select value={autoEmp} onValueChange={setAutoEmp}>
                <SelectTrigger className="rounded-xl mt-1" data-testid="auto-report-employee"><SelectValue placeholder="Seleziona"/></SelectTrigger>
                <SelectContent>
                  {employees.map((e) => <SelectItem key={e.id} value={e.id}>{e.name} {e.lastname}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Data</Label>
              <Input type="date" className="rounded-xl mt-1" value={autoDate} onChange={(e)=>setAutoDate(e.target.value)} data-testid="auto-report-date"/>
            </div>
            {autoStatus && (
              <div
                data-testid="auto-report-status"
                className={`rounded-xl p-3 text-xs border-2 ${
                  autoStatus.kind === "ok" ? "bg-green-50 border-green-300 text-green-800" :
                  autoStatus.kind === "empty" ? "bg-amber-50 border-amber-300 text-amber-900" :
                  autoStatus.kind === "loading" ? "bg-neutral-50 border-neutral-200 text-neutral-700" :
                  "bg-red-50 border-red-300 text-red-800"
                }`}
              >
                <div className="font-semibold mb-1">
                  {autoStatus.kind === "ok" && "✓ Fatto"}
                  {autoStatus.kind === "empty" && "⚠ Nessuna timbratura"}
                  {autoStatus.kind === "error" && "✗ Errore"}
                  {autoStatus.kind === "loading" && "Caricamento…"}
                </div>
                <div>{autoStatus.msg}</div>
                {autoStatus.lastDay && (
                  <button
                    type="button"
                    onClick={() => { setAutoDate(autoStatus.lastDay); setAutoStatus(null); }}
                    data-testid="auto-report-jump-to-last-day"
                    className="mt-2 underline font-semibold hover:no-underline"
                  >
                    → Usa data {new Date(autoStatus.lastDay).toLocaleDateString("it-IT")} (ultima timbratura)
                  </button>
                )}
              </div>
            )}
          </div>
          <DialogFooter className="gap-2">
            <Button onClick={()=>setAutoOpen(false)} variant="ghost" className="rounded-full">Annulla</Button>
            <Button onClick={generateFromAttendances} disabled={autoBusy} data-testid="auto-report-generate" className="rounded-full bg-[#3B82F6] hover:bg-[#7C3AED]">
              {autoBusy ? "Generazione…" : "Genera"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <div className="card-smart overflow-hidden">
        <table className="w-full text-sm">
          <thead className="border-b border-[#1F2540]">
            <tr className="text-left">
              <th className="p-4 font-semibold">Data</th>
              <th className="p-4 font-semibold">Dipendente</th>
              <th className="p-4 font-semibold">Commessa</th>
              <th className="p-4 font-semibold">Mattina</th>
              <th className="p-4 font-semibold">Pomeriggio</th>
              <th className="p-4 font-semibold">Stato</th>
              <th className="p-4"></th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && <tr><td colSpan="7" className="p-12 text-center text-[#757575]"><ClipboardList className="w-10 h-10 mx-auto mb-3 text-[#3B82F6]/50"/>Nessun rapportino.</td></tr>}
            {items.map((r) => (
              <tr key={r.id} className="border-b border-[#1F2540]" data-testid={`report-${r.id}`}>
                <td className="p-4">{new Date(r.date).toLocaleDateString("it-IT")}</td>
                <td className="p-4 font-semibold">{empName(r.employee_id)}</td>
                <td className="p-4 text-xs font-mono text-[#3B82F6]">{ordName(r.order_id)}</td>
                <td className="p-4 text-xs">{hhmm(r.start_morning)} – {hhmm(r.end_morning)}</td>
                <td className="p-4 text-xs">{hhmm(r.start_after)} – {hhmm(r.end_after)}</td>
                <td className="p-4">
                  {r.disease && <span className="text-[10px] bg-red-100 text-red-700 px-2 py-0.5 rounded-full mr-1">Malattia</span>}
                  {r.holiday_hours > 0 && <span className="text-[10px] bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full mr-1">Ferie {r.holiday_hours}h</span>}
                  {r.availability && <span className="text-[10px] bg-[#1E2347] text-[#7C3AED] px-2 py-0.5 rounded-full">Reperib.</span>}
                </td>
                <td className="p-4 text-right">
                  <Button size="icon" variant="ghost" onClick={()=>del(r.id)} className="hover:bg-red-50 hover:text-red-600 rounded-full h-8 w-8"><Trash2 className="w-3.5 h-3.5"/></Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
